import React, { useState } from "react";
import { Modal, Button, Alert, Badge, Table, Spinner } from "react-bootstrap";
import { ListingDetailEntity } from "../dto/listing/ListingDetailEntity";
import { manualPayout } from "../controller/ListingController";
import toast from "react-hot-toast";

interface Props {
    show: boolean;
    listingDetail: ListingDetailEntity;
    onClose: () => void;
    onSuccess: () => void;
}

export const ListingPayoutModal: React.FC<Props> = ({ show, listingDetail, onClose, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState<"review" | "confirm">("review");

    const listing = listingDetail.listing;

    const handleClose = () => {
        if (loading) return;
        setStep("review");
        onClose();
    };

    const handlePayout = async () => {
        if (!listing?.id) return;
        setLoading(true);
        try {
            const response = await manualPayout(listing.id);
            if (response.status !== 0) {
                toast.error(response.msg);
            } else {
                toast.success(`Payout for listing #${listing.id} completed.`);
                handleClose();
                onSuccess();
            }
        } catch (e: any) {
            toast.error(e.message ?? "Unexpected error");
        } finally {
            setLoading(false);
        }
    };

    const currency = listing?.currency ?? "MNT";
    const winningAmount = listing?.currentPrice;

    return (
        <Modal show={show} onHide={handleClose} centered backdrop={loading ? "static" : true}>
            <Modal.Header style={{ background: "#d1e7dd", borderBottom: "2px solid #198754" }}>
                <Modal.Title className="d-flex align-items-center gap-2">
                    <span style={{ fontSize: "1.3rem" }}>💸</span>
                    <span style={{ fontWeight: 700, color: "#0a3622" }}>Manual Payout</span>
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {step === "review" ? (
                    <>
                        <Table bordered size="sm" className="mb-3">
                            <tbody>
                                <tr>
                                    <th style={{ width: "40%", background: "#f8f9fa" }}>Listing</th>
                                    <td>#{listing?.id}</td>
                                </tr>
                                <tr>
                                    <th style={{ background: "#f8f9fa" }}>Product</th>
                                    <td>{listing?.productTitle ?? "—"}</td>
                                </tr>
                                <tr>
                                    <th style={{ background: "#f8f9fa" }}>Status</th>
                                    <td><Badge bg="secondary">{listing?.status}</Badge></td>
                                </tr>
                                <tr>
                                    <th style={{ background: "#f8f9fa" }}>Seller</th>
                                    <td>{listing?.accountNickname ?? listing?.accountPhoneNo ?? "—"}</td>
                                </tr>
                                <tr>
                                    <th style={{ background: "#f8f9fa" }}>Winning Bid ID</th>
                                    <td>{listing?.winningBidId ?? "—"}</td>
                                </tr>
                                <tr>
                                    <th style={{ background: "#f8f9fa" }}>Payout Amount</th>
                                    <td>
                                        <strong style={{ color: "#198754", fontSize: "1.05rem" }}>
                                            {currency} {winningAmount?.toLocaleString() ?? "—"}
                                        </strong>
                                    </td>
                                </tr>
                            </tbody>
                        </Table>

                        <Alert variant="info" className="py-2 px-3" style={{ fontSize: "0.85rem" }}>
                            This captures the winning bidder's hold and transfers{" "}
                            <strong>{currency} {winningAmount?.toLocaleString()}</strong> to the seller's wallet.
                            This action cannot be undone.
                        </Alert>
                    </>
                ) : (
                    <div className="text-center py-3">
                        <div style={{ fontSize: "3rem", marginBottom: 8 }}>💸</div>
                        <p style={{ fontWeight: 600, fontSize: "1.05rem" }}>Are you absolutely sure?</p>
                        <p className="text-muted" style={{ fontSize: "0.9rem" }}>
                            <strong>{currency} {winningAmount?.toLocaleString()}</strong> will be transferred
                            to the seller's wallet. This cannot be reversed from this panel.
                        </p>
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer>
                {step === "review" ? (
                    <>
                        <Button variant="secondary" onClick={handleClose}>Cancel</Button>
                        <Button
                            variant="success"
                            onClick={() => setStep("confirm")}
                            disabled={listing?.status !== "ENDED" || !listing?.winningBidId}
                        >
                            Review &amp; Confirm →
                        </Button>
                    </>
                ) : (
                    <>
                        <Button variant="outline-secondary" onClick={() => setStep("review")} disabled={loading}>
                            ← Back
                        </Button>
                        <Button variant="success" onClick={handlePayout} disabled={loading}>
                            {loading
                                ? <><Spinner size="sm" animation="border" className="me-2" />Processing…</>
                                : "Confirm Payout"
                            }
                        </Button>
                    </>
                )}
            </Modal.Footer>
        </Modal>
    );
};